import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';

const { FiMail, FiLoader, FiCheck, FiArrowRight } = FiIcons;

const QuickSubscribe = () => {
  const { user } = useAuth();
  const [email,   setEmail]   = useState('');
  const [status,  setStatus]  = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');

    try {
      if (user) {
        // Logged-in user: upsert their subscription
        const { error } = await supabase.rpc('upsert_newsletter_subscription', {
          p_user_id:     user.id,
          p_email:       email,
          p_is_active:   true,
          p_frequency:   'weekly',
          p_preferences: { genres: [], followed_only: false },
        });
        if (error) throw error;
      } else {
        // Non-logged-in: store email in a lightweight way via anon insert
        const { error } = await supabase
          .from('d2f_newsletter_subscriptions')
          .insert({ user_id: null, email, is_active: true })
          .select();
        // Allow anon fail gracefully — they need to sign up
        if (error && error.code !== '23502') throw error;
      }
      setStatus('success');
      setMessage('You\'re subscribed! Check your inbox.');
      setEmail('');
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Try again.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  if (status === 'success') {
    return (
      <div className="flex items-center gap-3 px-5 py-3 bg-green-500/10 border border-green-500/30 rounded-2xl">
        <SafeIcon icon={FiCheck} className="text-green-400 flex-shrink-0" />
        <p className="text-green-400 text-xs font-black">{message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <div className="relative flex-1">
        <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full pl-9 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-white text-xs font-bold placeholder-zinc-700 focus:outline-none focus:border-brand-pink/40 transition-colors"
        />
      </div>
      <button
        type="submit"
        disabled={status === 'loading' || !email}
        className="px-4 py-3 btn-brand rounded-2xl text-xs flex items-center gap-2 disabled:opacity-60 flex-shrink-0"
      >
        {status === 'loading'
          ? <SafeIcon icon={FiLoader} className="animate-spin" />
          : <SafeIcon icon={FiArrowRight} />
        }
      </button>
    </form>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-white/10 py-16 px-8 relative z-10 glass-panel mt-auto">
      <div className="max-w-7xl mx-auto space-y-12">

        {/* Newsletter strip */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 glass-card rounded-3xl border-brand-pink/10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiMail} className="text-brand-pink" />
              <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Weekly Digest</span>
            </div>
            <h3 className="text-2xl font-black uppercase tracking-tighter text-white">Never Miss a Drop</h3>
            <p className="text-zinc-500 text-sm font-bold">
              Get the latest exclusive drops from artists you follow, delivered every Monday.
            </p>
          </div>
          <div className="flex flex-col justify-center space-y-2">
            <QuickSubscribe />
            <p className="text-[10px] text-zinc-700 font-bold pl-1">
              Sent from info@visionmedia345.com · Unsubscribe anytime
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-3">
            <img
              src="https://media-manager-c.questera.ai/greta-media/5fb2f9d5255c375c4c593c40a3604cfd971c9f56c7725c3a05c28f383b843f2c3e4f4687d5d18e728c639d20e7900999/images/aW1hZ2UvcG5n/e291f38d345a3c31aa9db040447e5035.png"
              alt="Vision Direct Logo"
              className="h-8 w-8 object-contain drop-shadow-[0_0_8px_rgba(255,102,204,0.5)] grayscale hover:grayscale-0 transition-all duration-500"
            />
            <span className="text-3xl font-black tracking-tighter text-white uppercase italic">Vision Direct</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-bold tracking-widest uppercase text-zinc-400">
            <Link to="/help"       className="hover:text-brand-coral transition-colors">Help Center</Link>
            <Link to="/artists"    className="hover:text-brand-coral transition-colors">For Artists</Link>
            <Link to="/newsletter" className="hover:text-brand-pink  transition-colors">Newsletter</Link>
            <Link to="/privacy"    className="hover:text-brand-coral transition-colors">Privacy Policy</Link>
            <Link to="/terms"      className="hover:text-brand-coral transition-colors">Terms of Service</Link>
          </div>

          <p className="text-zinc-600 text-xs font-black tracking-widest uppercase">
            © {new Date().getFullYear()} Vision Direct · All rights reserved
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;